import { TABLE_TOP_CORNERS } from "../../v2/components/tableHeaderCorners.js";
// src/features/reports/SpaceFitReport.jsx
//
// SPACE-FIT-REPORT-S2-POPULATE: Space Fit report — live KPI row, sortable
// table, and quadrant scatter chart.
//
// Data source: getSpaceFitReport() from reportsClient.js which calls
//   getLocations() client-side and maps through computeSpaceFit().
//   No new DB queries or migrations.
//
// Views: Table (default) | Chart (segmented control toggle)
// Feature flag: report-space-fit  Role: viewer+

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { T } from "../../app/tokens.js";
import { OverflowTable } from "../../v2/components/ListOverflow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { ReportShell } from "./ReportShell.jsx";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import { ConsolidatedFilter } from "../../v2/components/ConsolidatedFilter.jsx";
import { SortControl } from "../../v2/components/SortControl.jsx";
import { fmtSqft } from "../../utils/format.js";
import {
  SPACE_FIT_VERDICT_OVER,
  SPACE_FIT_VERDICT_TIGHT,
  SPACE_FIT_VERDICT_DENSE,
  SPACE_FIT_VERDICT_UNDER,
  SPACE_FIT_VERDICT_RIGHT,
  SPACE_FIT_SEVERITY,
  SPACE_FIT_LABEL,
  SPACE_FIT_BG,
  SPACE_FIT_COLOR,
  SPACE_FIT_BORDER,
  SPACE_FIT_HEX,
  DESK_BALANCED_MIN,
  DESK_OVER_THRESHOLD,
} from "../../utils/spaceFitVerdict.js";
import { seatFitBands } from "../../services/seatFit.js";
import { useSpacePolicy } from "../../services/spacePolicyStore.js";
import { SeatFitKey } from "../../components/SeatFitKey.jsx";
import { SeatDensityBar, sfPerInstalledSeat } from "../../components/SeatDensityBar.jsx";
import { buildLocationUrl } from "../../services/buildUrl.js";
import { getSpaceFitReport } from "../../data/reportsClient.js";
import { LEASE_TYPES, LEASE_DISPLAY } from "../../utils/leaseTypeConstants.js";
import { useReportViewState } from "../../v2/hooks/useReportViewState.js";
import { REPORT_SPACE_FIT_KEY } from "../../services/portfolioStatePersistence.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { getReportNavEligibility } from "./reportNavEligibility.js";
import { ReportAccessNotice } from "./ReportShell.jsx";

// ─── Constants ────────────────────────────────────────────────────────────────

const DENSITY_COMFORTABLE = "comfortable";
const DENSITY_COMPACT = "compact";

// Field descriptors for useReportViewState — defines URL params, valid values,
// and defaults for all display state in SpaceFitReport.
const SF_VIEW_STATE_FIELDS = [
  { param: "view",      valid: ["table", "chart"],                                         default: "table"       },
  { param: "density",   valid: ["comfortable", "compact"],                                 default: "comfortable" },
  { param: "q",                                                                             default: ""            },
  { param: "sort",      valid: ["verdict", "name", "sf", "seats", "people", "sfPerSeat"],  default: "verdict"     },
  { param: "dir",       valid: ["asc", "desc"],                                             default: "asc"         },
  { param: "region",    array: true,                                                        default: []            },
  { param: "verdict",   array: true,                                                        default: []            },
  { param: "dense", array: true, default: [] },
  { param: "hub",       array: true,                                                        default: []            },
  { param: "leaseType", array: true,                                                        default: []            },
];

// KPI tile definitions — order and colors per mockup.
const KPI_DEFS = [
  { key: "assessed", label: "Offices assessed",                        foot: "with a verdict",         numColor: T.blue },
  { key: "over",     label: SPACE_FIT_LABEL[SPACE_FIT_VERDICT_OVER].full,  foot: "more people than desks", numColor: "var(--v2-red)" },
  { key: "tight", label: SPACE_FIT_VERDICT_TIGHT, foot: "near installed limit", numColor: T.ragAmberText },
  { key: "dense",    label: "Dense", foot: "independent density flag", numColor: T.mid },
  { key: "under",    label: SPACE_FIT_LABEL[SPACE_FIT_VERDICT_UNDER].full, foot: "empty desks",            numColor: T.dark },
  { key: "right",    label: SPACE_FIT_LABEL[SPACE_FIT_VERDICT_RIGHT].full, foot: "comfortable + subscribed", numColor: "var(--v2-green)" },
];

// Sort fields for the SortControl dropdown.
const SF_SORT_FIELDS = [
  { value: "verdict",   label: "Space Fit verdict", numeric: false },
  { value: "name",      label: "Office name",        numeric: false },
  { value: "sf",        label: "Sq ft",              numeric: true  },
  { value: "seats",     label: "Seats",              numeric: true  },
  { value: "people",    label: "People",             numeric: true  },
  { value: "sfPerSeat", label: "SF / seat",          numeric: true  },
];

// ─── Sort helpers ─────────────────────────────────────────────────────────────

function verdictSeverity(v) {
  return v != null ? (SPACE_FIT_SEVERITY[v] ?? 99) : 99;
}

function sortOffices(offices, key, dir, marketsEnabled = true) {
  const mult = dir === "asc" ? 1 : -1;
  return [...offices].sort((a, b) => {
    if (key === "verdict") {
      const sv = verdictSeverity(a.verdict) - verdictSeverity(b.verdict);
      if (sv !== 0) return sv; // severity order is always asc (worst first)
      // Secondary: SF desc
      const sa = a.sf ?? -Infinity;
      const sb = b.sf ?? -Infinity;
      return sb - sa;
    }
    if (key === "name") {
      const na = marketsEnabled
        ? `${a.market} ${a.locationAlias ?? ""}`.trim().toLowerCase()
        : (a.locationAlias || a.locationId || "").toLowerCase();
      const nb = marketsEnabled
        ? `${b.market} ${b.locationAlias ?? ""}`.trim().toLowerCase()
        : (b.locationAlias || b.locationId || "").toLowerCase();
      return mult * na.localeCompare(nb);
    }
    // Numeric fields — nulls always last
    const va = a[key] ?? (dir === "asc" ? Infinity : -Infinity);
    const vb = b[key] ?? (dir === "asc" ? Infinity : -Infinity);
    return mult * (va - vb);
  });
}

// ─── Filter helpers ───────────────────────────────────────────────────────────

function filterOffices(offices, search, selections, marketsEnabled = true) {
  const q = search.trim();
  const regions    = marketsEnabled ? selections.region ?? [] : [];
  const verdicts   = selections.verdict   ?? [];
  const hubs       = selections.hub       ?? [];
  const leaseTypes = selections.leaseType ?? [];
  return offices.filter((o) => {
    if (regions.length  && !regions.includes(o.region))   return false;
    if (verdicts.length && !verdicts.includes(o.verdict ?? "—")) return false;
    if (selections.dense?.length && !selections.dense.includes(o.dense ? "dense" : "not_dense")) return false;
    if (hubs.length) {
      const isHub = o.isHub === true;
      const matchesHub = (hubs.includes("Hub") && isHub) || (hubs.includes("Non-Hub") && !isHub);
      if (!matchesHub) return false;
    }
    if (leaseTypes.length) {
      const types = o.activeLeaseTypes ?? [];
      if (types.length === 0) {
        if (!leaseTypes.includes("__none__")) return false;
      } else if (!types.some((t) => leaseTypes.includes(t))) return false;
    }
    if (q) {
      if (!matchesFoldedQuery(q, marketsEnabled ? o.market : "", o.locationAlias, o.locationId)) return false;
    }
    return true;
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function VerdictPill({ verdict }) {
  if (!verdict) return <span style={{ color: T.mid }}>—</span>;
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      fontSize: T.fsXs,
      fontWeight: T.fwSemibold,
      padding: "2px 10px",
      borderRadius: 6,
      whiteSpace: "nowrap",
      background: SPACE_FIT_BG[verdict],
      color: SPACE_FIT_COLOR[verdict],
    }}>
      {SPACE_FIT_LABEL[verdict]?.full ?? verdict}
    </span>
  );
}

function DeskFitCell({ people, seats, deskDelta }) {
  if (seats == null) return <span style={{ color: T.mid }}>—</span>;
  if (people == null) return <span style={{ color: T.mid }}>—</span>;
  // displayDelta = seats - people (positive = surplus seats, negative = overflow)
  const displayDelta = -deskDelta;
  const sign = displayDelta > 0 ? "+" : "";
  const deltaColor = displayDelta > 0 ? "var(--v2-green)" : displayDelta < 0 ? "var(--v2-red)" : T.mid;
  return (
    <span>
      {people}/{seats}{" "}
      <span style={{ color: deltaColor, fontWeight: T.fwBold }}>
        {sign}{displayDelta}
      </span>
    </span>
  );
}

function CompactDensitySwitch({ checked, onChange, testId = "sf-density-toggle" }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={onChange}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 9,
        fontSize: T.fsSm5,
        fontWeight: T.fwBold,
        color: checked ? T.accent : T.mid,
        cursor: "pointer",
        userSelect: "none",
        background: "none",
        border: "none",
        padding: 0,
      }}
    >
      <span style={{
        position: "relative",
        width: 34,
        height: 20,
        borderRadius: 999,
        background: checked ? T.accent : T.switchOff,
        transition: "background 0.15s",
        flexShrink: 0,
        display: "inline-block",
        verticalAlign: "middle",
        border: "none",
        padding: 0,
        cursor: "pointer",
      }} aria-hidden="true">
        <span style={{
          position: "absolute",
          top: 2,
          left: checked ? 16 : 2,
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: T.white,
          boxShadow: "0 1px 2px rgba(16,36,58,0.3)",
          transition: "left 0.15s",
          pointerEvents: "none",
        }} />
      </span>
      Compact
    </button>
  );
}

function SegmentedControl({ value, onChange }) {
  const btn = (v, label) => (
    <button
      key={v}
      type="button"
      data-testid={`sf-view-${v}`}
      onClick={() => onChange(v)}
      style={{
        fontSize: T.fsSm5,
        fontWeight: T.fwBold,
        padding: "5px 14px",
        borderRadius: 6,
        border: "none",
        cursor: "pointer",
        background: value === v ? T.card : "transparent",
        color: value === v ? T.dark : T.mid,
        boxShadow: value === v ? "0 1px 2px rgba(15,23,42,.08)" : "none",
        transition: "all 0.12s",
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{
      display: "inline-flex",
      background: T.lightGray,
      border: `1px solid ${T.border}`,
      borderRadius: 8,
      padding: 2,
    }}>
      {btn("table", "Table")}
      {btn("chart", "Chart")}
    </div>
  );
}

// ─── Fit × Density map (mock Option C) ───────────────────────────────────────

// Verdict hex colors for SVG — shared Space Fit band colours.
const VERDICT_HEX = SPACE_FIT_HEX;

const CHART_W = 760;
const CHART_H = 440;
const PLOT_L = 56;
const PLOT_R = 736;
const PLOT_T = 24;
const PLOT_B = 394;
const X_MAX_PCT = 130;
const Y_MAX_SF = 450;

function xScale(pct)  { return PLOT_L + (Math.max(0, Math.min(pct, X_MAX_PCT)) / X_MAX_PCT) * (PLOT_R - PLOT_L); }
function yScale(sfps) { return PLOT_B - (Math.max(0, Math.min(sfps, Y_MAX_SF)) / Y_MAX_SF) * (PLOT_B - PLOT_T); }

/** Deterministic SVG label placement. Reserve region captions and all markers,
 * then move labels (not data points) with leaders when a cluster needs room. */
export function fitDensityLabels(points, floor) {
  const occupied = points.map(p => {
    const x = xScale(p.pct), y = yScale(p.sps);
    return { left: x - 9, right: x + 9, top: y - 9, bottom: y + 9 };
  });
  if (Number.isFinite(floor)) occupied.push({
    left: PLOT_R - 120, right: PLOT_R, top: yScale(floor) - 18, bottom: yScale(floor) - 2,
  });
  const result = new Map();
  const intersects = (a, b) => a.left < b.right + 3 && a.right > b.left - 3
    && a.top < b.bottom + 3 && a.bottom > b.top - 3;
  for (const p of [...points].sort((a, b) => yScale(a.sps) - yScale(b.sps) || a.label.localeCompare(b.label))) {
    const cx = xScale(p.pct), cy = yScale(p.sps);
    const width = p.label.length * 6;
    const candidates = [];
    for (let step = 0; step < 28; step++) {
      const dy = step === 0 ? 0 : (step % 2 ? -1 : 1) * Math.ceil(step / 2) * 16;
      for (const side of [1, -1]) {
        const left = Math.max(PLOT_L + 3, Math.min(PLOT_R - width - 3, side === 1 ? cx + 12 : cx - width - 12));
        const y = Math.max(PLOT_T + 38, Math.min(PLOT_B - 24, cy + 3 + dy));
        const box = { left, right: left + width, top: y - 11, bottom: y + 3 };
        candidates.push({ ...box, x: left, y, collisions: occupied.filter(b => intersects(box, b)).length,
          distance: Math.abs(dy) + Math.abs(left - cx) * 0.05 });
      }
    }
    const chosen = candidates.sort((a, b) => a.collisions - b.collisions || a.distance - b.distance)[0];
    result.set(p.o.id, chosen);
    occupied.push(chosen);
  }
  return result;
}

export const DENSITY_REGION = Object.freeze({
  CROWDED: "Crowded",
  FULL: "Full, room to densify",
  TOO_MUCH: "Too much space",
  PACKED: "Packed but empty",
});

/** Pure presentation region from unrounded ratios. pct in percent. null = unshaded middle. */
export function densityRegion(pct, sps, floor) {
  if (pct == null || sps == null || !Number.isFinite(pct) || !Number.isFinite(sps) || !Number.isFinite(floor)) return null;
  const below = sps < floor;
  if (pct > 100 && below) return DENSITY_REGION.CROWDED;
  if (pct >= 90 && !below) return DENSITY_REGION.FULL;
  if (pct < 80 && !below) return DENSITY_REGION.TOO_MUCH;
  if (pct < 80 && below) return DENSITY_REGION.PACKED;
  return null;
}

/** Pure: chart point for an office, or null when inputs are missing. */
export function fitDensityPoint(o) {
  const sps = sfPerInstalledSeat(o.sf, o.seats);
  if (sps == null || o.people == null || !Number.isFinite(Number(o.people))) return null;
  const pct = (Number(o.people) / Number(o.seats)) * 100;
  return { pct, sps, offX: pct > X_MAX_PCT, offY: sps > Y_MAX_SF };
}

const REGION_LEGEND = [
  { key: DENSITY_REGION.CROWDED,  fill: T.ragRedBg,   ink: "var(--v2-red)", note: "Over seats and below floor. Act first." },
  { key: DENSITY_REGION.FULL,     fill: T.ragAmberBg, ink: T.ragAmberText,  note: "Near or over seats, but space allows more desks." },
  { key: DENSITY_REGION.TOO_MUCH, fill: T.border,     ink: T.planCompFill,  note: "Under-used and roomy. Consolidate or sublease." },
  { key: DENSITY_REGION.PACKED,   fill: T.lightGray,  ink: T.mid,           note: "Dense layout, few people. Re-plan the layout." },
];

export function SpaceFitScatterChart({ offices, marketsEnabled = true, onOfficeClick }) {
  const { baseline: policy } = useSpacePolicy();
  const floor = Number.isFinite(policy?.sfFloor) ? policy.sfFloor : null;
  const references = policy ? [policy.underUsedPct, policy.tightPct ?? 100 - policy.hcBufferPct, 100] : [];
  const [active, setActive] = useState(null);

  const points = useMemo(() => {
    const cityCount = {};
    offices.forEach((o) => { const c = o.market ?? ""; cityCount[c] = (cityCount[c] ?? 0) + 1; });
    return offices.map((o) => {
      const p = fitDensityPoint(o);
      if (!p) return null;
      const city = marketsEnabled ? (o.market || o.locationAlias || o.id) : (o.locationAlias || o.locationId || o.id);
      const alias = o.locationAlias ? o.locationAlias.split(/\s+/).slice(0, 2).join(" ") : "";
      const label = marketsEnabled && cityCount[o.market] > 1 && alias ? `${city} ${alias}` : city;
      const full = marketsEnabled && o.locationAlias ? `${o.market} · ${o.locationAlias}` : city;
      return { o, ...p, label, full, region: densityRegion(p.pct, p.sps, floor) };
    }).filter(Boolean);
  }, [offices, marketsEnabled, floor]);

  const omitted = offices.length - points.length;
  const labels = useMemo(() => fitDensityLabels(points, floor), [points, floor]);
  const yFloor = floor != null ? yScale(floor) : null;
  const open = (o) => { if (onOfficeClick) onOfficeClick(o.id, o); };
  const tip = (p) => `${p.full}: ${Math.round(p.pct)}%, ${Math.round(p.sps).toLocaleString()} sf/seat`;

  const rect = (testid, x1, x2, y1, y2, fill) => (
    <rect data-testid={testid} x={x1} y={y1} width={Math.max(0, x2 - x1)} height={Math.max(0, y2 - y1)} fill={fill} />
  );

  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 2 }}>Fit × density</div>
      <div style={{ fontSize: T.fsSm5, color: T.mid, marginBottom: 14 }}>
        Space Fit across, sf per installed seat up. Dot colour is the Space Fit band.
      </div>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ position: "relative", flex: "1 1 520px", minWidth: 0 }}>
          <svg data-testid="sf-scatter-chart" viewBox={`0 0 ${CHART_W} ${CHART_H}`}
            style={{ width: "100%", height: "auto", display: "block", overflow: "visible" }}
            role="group" aria-label="Offices plotted by Space Fit and SF per seat">
            {floor != null && (<>
              {rect("sf-region-crowded", xScale(100), xScale(X_MAX_PCT), yFloor, PLOT_B, T.ragRedBg)}
              {rect("sf-region-full", xScale(90), xScale(X_MAX_PCT), PLOT_T, yFloor, T.ragAmberBg)}
              {rect("sf-region-too-much", xScale(0), xScale(80), PLOT_T, yFloor, T.border)}
              {rect("sf-region-packed", xScale(0), xScale(80), yFloor, PLOT_B, T.lightGray)}
            </>)}
            {references.map((p) => (
              <g key={p}>
                <line data-testid={`sf-ref-${p}`} x1={xScale(p)} x2={xScale(p)} y1={PLOT_T} y2={PLOT_B} stroke={T.borderDark} strokeDasharray="3 3" />
                <text x={xScale(p)} y={PLOT_B + 16} fontSize={10} fill={T.mid} textAnchor="middle">{p}%</text>
              </g>
            ))}
            {[0, 130].map((p) => <text key={p} x={xScale(p)} y={PLOT_B + 16} fontSize={10} fill={T.light} textAnchor="middle">{p}%</text>)}
            {[0, 100, 200, 300, 400].map((v) => (
              <text key={v} x={PLOT_L - 8} y={yScale(v) + 3} fontSize={10} fill={T.mid} textAnchor="end">{v}</text>
            ))}
            <line x1={PLOT_L} x2={PLOT_R} y1={PLOT_B} y2={PLOT_B} stroke={T.borderDark} />
            {floor != null && (<>
              <line data-testid="sf-floor-line" x1={PLOT_L} x2={PLOT_R} y1={yFloor} y2={yFloor} stroke={T.pendingAmber} strokeWidth={1.5} />
              <text x={PLOT_R} y={yFloor - 5} fontSize={10} fill={T.ragAmberText} textAnchor="end">{Math.round(floor)} sf/seat floor</text>
              <text x={PLOT_R - 6} y={PLOT_B - 8} fontSize={11} fontWeight={700} fill="var(--v2-red)" textAnchor="end">{DENSITY_REGION.CROWDED}</text>
              <text x={PLOT_R - 6} y={PLOT_T + 14} fontSize={11} fontWeight={700} fill={T.ragAmberText} textAnchor="end">{DENSITY_REGION.FULL}</text>
              <text x={PLOT_L + 6} y={PLOT_T + 14} fontSize={11} fontWeight={700} fill={T.planCompFill}>{DENSITY_REGION.TOO_MUCH}</text>
              <text x={PLOT_L + 6} y={PLOT_B - 8} fontSize={11} fontWeight={700} fill={T.mid}>{DENSITY_REGION.PACKED}</text>
            </>)}
            <text x={(PLOT_L + PLOT_R) / 2} y={CHART_H - 8} fontSize={11} fill={T.planCompFill} textAnchor="middle">Space Fit: assigned ÷ installed seats</text>
            <text transform={`translate(14,${(PLOT_T + PLOT_B) / 2}) rotate(-90)`} fontSize={11} fill={T.planCompFill} textAnchor="middle">SF per seat</text>

            {points.map((p) => {
              const cx = xScale(p.pct);
              const cy = yScale(p.sps);
              const position = labels.get(p.o.id);
              const color = p.o.verdict && VERDICT_HEX[p.o.verdict] ? VERDICT_HEX[p.o.verdict] : T.light;
              const label = tip(p);
              return (
                <g key={p.o.id} data-testid={`sf-dot-${p.o.id}`} data-region={p.region ?? ""}
                  data-off-scale={p.offX || p.offY ? "true" : "false"}
                  role="button" tabIndex={0} aria-label={label} style={{ cursor: "pointer", outline: "none" }}
                  onMouseEnter={() => setActive(p.o.id)} onMouseLeave={() => setActive(null)}
                  onFocus={() => setActive(p.o.id)} onBlur={() => setActive(null)}
                  onClick={() => open(p.o)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(p.o); } }}>
                  <title>{label}</title>
                  <line aria-hidden="true" x1={cx} y1={cy}
                    x2={Math.max(position.left, Math.min(position.right, cx))} y2={position.y - 4}
                    stroke={T.borderDark} strokeWidth={0.75} />
                  <circle cx={cx} cy={cy} r={active === p.o.id ? 8 : 6} fill={color} stroke={T.card} strokeWidth={1.5} />
                  {p.offY && <text data-testid={`sf-dot-${p.o.id}-up`} x={cx} y={cy - 9} fontSize={9} fill={T.mid} textAnchor="middle">▲</text>}
                  {p.offX && <text data-testid={`sf-dot-${p.o.id}-right`} x={cx - 10} y={cy + 3} fontSize={9} fill={T.mid} textAnchor="end" transform={`rotate(90, ${cx - 10}, ${cy})`}>▲</text>}
                  <text data-testid={`sf-label-${p.o.id}`} x={position.x} y={position.y}
                    fontSize={10} fill={T.planCompFill} textAnchor="start">{p.label}</text>
                </g>
              );
            })}
          </svg>
          {active != null && (() => {
            const p = points.find((q) => q.o.id === active);
            if (!p) return null;
            const left = (xScale(p.pct) / CHART_W) * 100;
            const top = (yScale(p.sps) / CHART_H) * 100;
            return (
              <div role="tooltip" data-testid="sf-chart-tooltip" style={{
                position: "absolute", left: `${left}%`, top: `${top}%`,
                transform: `translate(${left > 65 ? "calc(-100% - 12px)" : "12px"}, calc(-100% - 6px))`,
                background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 10px",
                boxShadow: T.shadowTooltip, color: T.dark, fontSize: T.fsXs, whiteSpace: "nowrap", pointerEvents: "none", zIndex: 5,
              }}>{tip(p)}</div>
            );
          })()}
          {omitted > 0 && (
            <div data-testid="sf-chart-omitted" style={{ fontSize: T.fsXs, color: T.light, marginTop: 8 }}>
              {omitted} office{omitted !== 1 ? "s" : ""} omitted: missing sf, installed seats or assigned people.
            </div>
          )}
        </div>
        <div style={{ flex: "0 1 240px", fontSize: T.fsXs, color: T.mid }} data-testid="sf-chart-legend">
          {REGION_LEGEND.map((r) => (
            <div key={r.key} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, flex: "none", marginTop: 2, background: r.fill, border: `1px solid ${T.border}` }} />
              <span><b style={{ display: "block", color: r.ink }}>{r.key}</b>{r.note}</span>
            </div>
          ))}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 6 }}>
            {[SPACE_FIT_VERDICT_OVER, SPACE_FIT_VERDICT_TIGHT, SPACE_FIT_VERDICT_RIGHT, SPACE_FIT_VERDICT_UNDER].map((v) => (
              <span key={v} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: VERDICT_HEX[v] }} />{SPACE_FIT_LABEL[v].full}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SpaceFitReport ───────────────────────────────────────────────────────────

export function migrateLegacyFitFilters(verdict, dense = []) {
  if (verdict.includes("Dense")) return { verdict: [], dense: verdict.length === 1 ? ["dense"] : [] };
  if (verdict.includes("Over-Capacity")) return { verdict: verdict.map(v => v === "Over-Capacity" ? "Over-Cap" : v), dense };
  return null;
}

export function SpaceFitReport({ onOfficeClick, onBack, prevReport = null, nextReport = null, onNavigateReport }) {
  const policyState = useSpacePolicy();
  const featureContext = useFeatureFlagContext();
  const eligibility = getReportNavEligibility(featureContext);
  const officesEnabled = eligibility.offices;
  const marketsEnabled = eligibility.markets;
  const [data,      setData]      = useState(null);   // { offices, kpis }
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  // Display state encoded in URL + persisted to localStorage.
  // URL params take precedence on mount; localStorage restores last view on
  // return navigation; defaults apply when both are absent.
  const [rvs, setRvs] = useReportViewState({
    storageKey: REPORT_SPACE_FIT_KEY,
    fields: SF_VIEW_STATE_FIELDS,
  });
  useEffect(() => {
    const patch = migrateLegacyFitFilters(rvs.verdict, rvs.dense);
    if (patch) setRvs(patch);
  }, [rvs.verdict, rvs.dense, setRvs]);

  // Derive named aliases so existing render code needs minimal changes.
  const search    = rvs.q;
  const sortCol   = rvs.sort;
  const sortDir   = rvs.dir;
  const view      = rvs.view;
  const density   = rvs.density;
  const selections = {
    region:    rvs.region,
    verdict:   rvs.verdict,
    dense: rvs.dense,
    hub:       rvs.hub,
    leaseType: rvs.leaseType,
  };

  const isCompact = density === DENSITY_COMPACT;
  // userHasSetSort: derived from state; true when sort is not at its defaults.
  const userHasSetSort = sortCol !== "verdict" || sortDir !== "asc";

  useEffect(() => {
    if (!eligibility.ready || !officesEnabled) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSpaceFitReport()
      .then((report) => {
        if (cancelled) return;
        setData(report);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message ?? "Failed to load Space Fit data.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [eligibility.ready, officesEnabled, policyState.baseline]);

  // ── Filter dimensions derived from loaded data ─────────────────────────────

  const filterDimensions = useMemo(() => {
    if (!data) return [];
    const offices = data.offices;

    const regionSet = new Set(offices.map((o) => o.region).filter(Boolean));
    const regionOpts = [...regionSet].sort().map((r) => ({ value: r, label: r }));

    const verdictOpts = [
      ...seatFitBands().map(b => ({ value: b.label, label: b.label })),
      { value: "—",                     label: "No verdict (—)" },
    ].filter((opt) =>
      opt.value === "—"
        ? offices.some((o) => o.verdict == null)
        : offices.some((o) => o.verdict === opt.value)
    );

    const hubOpts = [
      { value: "Hub",     label: "Hub" },
      { value: "Non-Hub", label: "Non-Hub" },
    ];

    const leaseTypeOpts = [
      ...LEASE_TYPES.map((t) => ({ value: t, label: LEASE_DISPLAY[t] })),
      ...(offices.some((o) => !o.activeLeaseTypes?.length)
        ? [{ value: "__none__", label: "No active lease" }]
        : []),
    ];

    const dims = [];
    if (marketsEnabled && regionOpts.length > 1) dims.push({ id: "region",    label: "Region",     options: regionOpts });
    if (verdictOpts.length > 1) dims.push({ id: "verdict",   label: "Space Fit",  options: verdictOpts });
    dims.push({ id: "dense", label: "Density flag", options: [
      { value: "dense", label: "Dense" }, { value: "not_dense", label: "Not flagged" },
    ] });
    dims.push({ id: "hub",       label: "Hub",        options: hubOpts });
    dims.push({ id: "leaseType", label: "Lease type", options: leaseTypeOpts });
    return dims;
  }, [data, marketsEnabled]);

  // ── Filtered + sorted offices ──────────────────────────────────────────────

  const filtered = useMemo(() => {
    if (!data) return [];
    return filterOffices(data.offices, search, selections, marketsEnabled);
  }, [data, search, selections, marketsEnabled]);

  const sorted = useMemo(() => {
    return sortOffices(filtered, sortCol, sortDir, marketsEnabled);
  }, [filtered, sortCol, sortDir, marketsEnabled]);

  // ── Sort control ───────────────────────────────────────────────────────────

  const sortControlPrimary   = { key: sortCol, dir: sortDir };
  const sortControlSecondary = { key: null, dir: "asc" };

  const isNonDefault =
    search.trim() !== "" ||
    Object.values(selections).some((a) => a?.length > 0) ||
    userHasSetSort;

  function handleSortControlChange({ primary }) {
    setRvs({ sort: primary.key, dir: primary.dir });
  }

  const handleClear = useCallback(() => {
    setRvs({
      q:         "",
      region:    [],
      verdict:   [],
      dense: [],
      hub:       [],
      leaseType: [],
      sort:      "verdict",
      dir:       "asc",
    });
  }, [setRvs]);

  // ── Scroll-sync refs for sticky header/body split ──────────────────────────
  // Same pattern as HeadcountTrendReport.jsx.

  const sfHeaderRef = useRef(null);
  const sfBodyRef   = useRef(null);
  const handleSfBodyScroll = useCallback(() => {
    if (sfHeaderRef.current && sfBodyRef.current) {
      sfHeaderRef.current.scrollLeft = sfBodyRef.current.scrollLeft;
    }
  }, []);

  // Column widths for colgroup
  const COL_OFFICE  = 180;
  const COL_REGION  = marketsEnabled ? 70 : 0;
  const COL_SF      = 90;
  const COL_SEATS   = 65;
  const COL_PEOPLE  = 65;
  const COL_DESKFT  = isCompact ? 0 : 120;
  const COL_HROOM   = isCompact ? 0 : 90;
  const COL_SPSEAT  = 170;
  const COL_VERDICT = 120;
  const sfMinW = COL_OFFICE + COL_REGION + COL_SF + COL_SEATS + COL_PEOPLE +
                 COL_DESKFT + COL_HROOM + COL_SPSEAT + COL_VERDICT;

  // ── Render ─────────────────────────────────────────────────────────────────

  const kpis = data?.kpis ?? { total: 0, assessed: 0, over: 0, dense: 0, under: 0, right: 0 };

  if (!officesEnabled || !eligibility.ready) {
    return <ReportAccessNotice reportName="Space Fit" loading={!eligibility.ready} onBack={onBack} />;
  }

  return (
    <ReportShell
        recordNavigation
        onBack={onBack}
        backTestId="sf-back"
        prevReport={prevReport}
        nextReport={nextReport}
        onNavigateReport={onNavigateReport}
      >

      <div style={{ maxWidth: 1400, padding: "0 32px 48px", margin: "0 auto", width: "100%" }}>

        {/* KPI row */}
        {!loading && !error && (
          <div style={{ marginBottom: 16 }}>
          <KpiBand testId="sf-kpi-row" ariaLabel="Report metrics">
            {KPI_DEFS.map((def) => (
              <KpiTile
                key={def.key}
                label={def.label}
                value={kpis[def.key]}
                sub={def.key === "assessed" ? `of ${kpis.total} · operational + dormant` : def.foot}
                color={def.numColor}
                valueStyle={{ fontSize: T.fsXl5, fontWeight: T.fwDisplay }}
              />
            ))}
          </KpiBand>
          </div>
        )}
        {!loading && !error && (
          <div data-testid="sf-period-label" style={{ color: T.mid, fontSize: T.fsSm, margin: "-8px 0 14px" }}>
            Space Fit = assigned today vs installed seats.
          </div>
        )}

        {/* Toolbar */}
        <div
          data-testid="sf-toolbar"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <SearchInput
            value={search}
            onChange={(v) => setRvs({ q: v })}
            placeholder="Search offices…"
            testId="sf-search"
          />
          <ConsolidatedFilter
            dimensions={filterDimensions}
            selections={selections}
            onChange={(newSel) => setRvs({
              region:    newSel.region    ?? [],
              verdict:   newSel.verdict   ?? [],
              dense: newSel.dense ?? [],
              hub:       newSel.hub       ?? [],
              leaseType: newSel.leaseType ?? [],
            })}
            testId="sf-filter"
          />
          <div style={{ display: view === "table" ? "block" : "none" }}>
            <SortControl
              fields={SF_SORT_FIELDS}
              primary={sortControlPrimary}
              secondary={sortControlSecondary}
              onChange={handleSortControlChange}
              testId="sf-sort"
              defaultKey="verdict"
            />
          </div>
          {isNonDefault && (
            <button
              type="button"
              data-testid="sf-clear-btn"
              onClick={handleClear}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.blueSoft; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              style={{
                background: "transparent",
                border: "1px solid transparent",
                borderRadius: 8,
                color: "var(--v2-accent)",
                fontSize: T.fsBase,
                fontWeight: T.fwBold,
                cursor: "pointer",
                padding: "7px 11px",
              }}
            >
              Clear
            </button>
          )}
          <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            {view === "table" && (
              <CompactDensitySwitch
                checked={isCompact}
                onChange={() => setRvs({ density: density === DENSITY_COMFORTABLE ? DENSITY_COMPACT : DENSITY_COMFORTABLE })}
                testId="sf-density-toggle"
              />
            )}
            <SegmentedControl value={view} onChange={(v) => setRvs({ view: v })} />
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div style={{ padding: "60px 0", textAlign: "center", color: T.mid, fontSize: T.fsBase }}>
            Loading…
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div style={{ padding: "40px 0", textAlign: "center", color: "var(--v2-red)", fontSize: T.fsBase }}>
            {error}
          </div>
        )}

        {/* Chart view */}
        {!loading && !error && view === "chart" && (
          <SpaceFitScatterChart offices={filtered} marketsEnabled={marketsEnabled} onOfficeClick={onOfficeClick ?? ((id) => { window.location.assign(buildLocationUrl(id)); })} />
        )}

        {/* Table view */}
        {!loading && !error && view === "table" && (
          <>
            <div style={{
              background: T.card,
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              ...TABLE_TOP_CORNERS,
              marginBottom: 18,
            }}>
              {/* Sticky header shell — NO overflow on this div (see HeadcountTrendReport pattern) */}
              <div style={{ position: "sticky", top: 0, zIndex: 4, background: T.lightGray, ...TABLE_TOP_CORNERS }}>
                <div ref={sfHeaderRef} style={{ overflowX: "hidden", ...TABLE_TOP_CORNERS }}>
                  <OverflowTable overflowScrollRef={sfBodyRef}
                    data-testid="sf-trend-header"
                    style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5, tableLayout: "fixed", minWidth: sfMinW }}
                  >
                    <colgroup>
                      <col style={{ width: COL_OFFICE }} />
                      {marketsEnabled && <col style={{ width: COL_REGION }} />}
                      <col style={{ width: COL_SF }} />
                      <col style={{ width: COL_SEATS }} />
                      <col style={{ width: COL_PEOPLE }} />
                      {!isCompact && <col style={{ width: COL_DESKFT }} />}
                      {!isCompact && <col style={{ width: COL_HROOM }} />}
                      <col style={{ width: COL_VERDICT }} />
                      <col style={{ width: COL_SPSEAT }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ ...TH, textAlign: "left", position: "sticky", left: 0, background: T.lightGray, zIndex: 4 }}>
                          Office
                        </th>
                        {marketsEnabled && <th style={{ ...TH, textAlign: "left" }} data-testid="sf-col-region">Region</th>}
                        <th style={{ ...TH, textAlign: "right" }} data-testid="sf-col-sf">SF</th>
                        <th style={{ ...TH, textAlign: "right" }} data-testid="sf-col-seats">Seats</th>
                        <th style={{ ...TH, textAlign: "right" }} data-testid="sf-col-people">People</th>
                        {!isCompact && (
                          <th style={{ ...TH, textAlign: "right" }} data-testid="sf-col-deskfit">
                            Desk fit
                          </th>
                        )}
                        {!isCompact && (
                          <th style={{ ...TH, textAlign: "right" }} data-testid="sf-col-headroom">
                            Expansion
                          </th>
                        )}
                        <th style={{ ...TH, textAlign: "left" }} data-testid="sf-col-verdict">
                          Space Fit
                        </th>
                        <th style={{ ...TH, textAlign: "left" }} data-testid="sf-col-spacefit">
                          <span title="Rentable sq ft per installed seat.">SF / seat</span>
                        </th>
                      </tr>
                    </thead>
                  </OverflowTable>
                </div>
              </div>

              {/* Scrollable body */}
              <div
                ref={sfBodyRef}
                onScroll={handleSfBodyScroll}
                style={{ overflowX: "auto", borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}
              >
                <table
                  data-testid="sf-trend-matrix"
                  style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5, tableLayout: "fixed", minWidth: sfMinW }}
                >
                  <colgroup>
                    <col style={{ width: COL_OFFICE }} />
                    {marketsEnabled && <col style={{ width: COL_REGION }} />}
                    <col style={{ width: COL_SF }} />
                    <col style={{ width: COL_SEATS }} />
                    <col style={{ width: COL_PEOPLE }} />
                    {!isCompact && <col style={{ width: COL_DESKFT }} />}
                    {!isCompact && <col style={{ width: COL_HROOM }} />}
                    <col style={{ width: COL_VERDICT }} />
                    <col style={{ width: COL_SPSEAT }} />
                  </colgroup>
                  <tbody>
                    {sorted.length === 0 ? (
                      <tr>
                        <td
                          data-testid="sf-empty-state"
                          colSpan={(isCompact ? 6 : 8) + (marketsEnabled ? 1 : 0)}
                          style={{
                            padding: "64px 24px",
                            textAlign: "center",
                            color: T.mid,
                            fontSize: T.fsBase,
                          }}
                        >
                          No offices match your filters.
                        </td>
                      </tr>
                    ) : (
                      sorted.map((o, idx) => (
                        <tr
                          key={o.id}
                          data-testid={`sf-row-${o.id}`}
                          style={{
                            borderBottom: idx < sorted.length - 1 ? `1px solid ${T.border}` : "none",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = T.hoverBg; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                        >
                          {/* Office — sticky left */}
                          <td style={{
                            ...TD,
                            position: "sticky",
                            left: 0,
                            background: "inherit",
                            zIndex: 1,
                            minWidth: COL_OFFICE,
                            padding: isCompact ? "7px 14px" : "10px 14px",
                          }}>
                            <span style={{ fontWeight: T.fwBold, color: T.dark }}>{marketsEnabled ? o.market : (o.locationAlias || o.locationId)}</span>
                            {o.locationAlias && (
                              <span style={{ color: T.mid, fontWeight: "normal" }}> · {o.locationAlias}</span>
                            )}
                          </td>

                          {marketsEnabled && <td style={{ ...TD, padding: isCompact ? "7px 14px" : "10px 14px" }}>
                            <span style={{ color: T.mid, fontSize: T.fsXxs }}>{o.region}</span>
                          </td>}

                          <td style={{ ...TD, textAlign: "right", padding: isCompact ? "7px 14px" : "10px 14px" }}>
                            {o.sf != null ? fmtSqft(o.sf) : <span style={{ color: T.mid }}>—</span>}
                          </td>

                          <td style={{ ...TD, textAlign: "right", padding: isCompact ? "7px 14px" : "10px 14px" }}>
                            {o.seats != null ? o.seats.toLocaleString() : <span style={{ color: T.mid }}>—</span>}
                          </td>

                          <td style={{ ...TD, textAlign: "right", padding: isCompact ? "7px 14px" : "10px 14px" }}>
                            {o.people != null ? o.people.toLocaleString() : <span style={{ color: T.mid }}>—</span>}
                          </td>

                          {!isCompact && (
                            <td style={{ ...TD, textAlign: "right", padding: "10px 14px" }}>
                              <DeskFitCell people={o.people} seats={o.seats} deskDelta={o.deskDelta} />
                            </td>
                          )}

                          {!isCompact && (
                            <td style={{ ...TD, textAlign: "right", padding: "10px 14px" }}>
                              {o.headroom != null
                                ? o.headroom.toLocaleString()
                                : <span style={{ color: T.mid }}>—</span>}
                            </td>
                          )}

                          <td style={{ ...TD, padding: isCompact ? "7px 14px" : "10px 14px" }}>
                            <VerdictPill verdict={o.verdict} />
                          </td>

                          <td style={{ ...TD, padding: isCompact ? "7px 14px" : "10px 14px" }}>
                            <SeatDensityBar value={sfPerInstalledSeat(o.sf, o.seats)} floor={policyState.baseline?.sfFloor} data-testid={`sf-density-${o.id}`} />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {sorted.length > 0 && (
              <div style={{ fontSize: T.fsXs, color: T.mid }}>
                Sorted by verdict severity (Over-Cap → Tight → Under-Used → Right-Sized), then by size.
                &ldquo;Desk fit&rdquo; ± = seats − assigned people.
                <SeatFitKey />
              </div>
            )}
          </>
        )}

      </div>
    </ReportShell>
  );
}

// ─── Shared cell styles ───────────────────────────────────────────────────────

const TH = {
  padding:       "10px 14px",
  fontSize:      T.fsXxs,
  letterSpacing: ".07em",
  textTransform: "uppercase",
  color:         "var(--v2-mid)",
  fontWeight:    T.fwBold,
  borderBottom:  "1px solid var(--v2-line)",
  whiteSpace:    "nowrap",
  position:      "sticky",
  top:           0,
  zIndex:        3,
  background:    "var(--v2-tint)",
};

const TD = {
  verticalAlign: "middle",
  fontSize:      T.fsSm5,
  color:         T.dark,
};
